// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type {
  Address,
  Coverage,
  Encounter,
  HumanName,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  RelatedPerson,
} from '@medplum/fhirtypes';
import type { Cms1500Address, Cms1500Form, Cms1500Name, Cms1500Relationship, Cms1500Sex } from './cms1500-form';
import {
  blankCms1500Form,
  blankCms1500Line,
  CMS1500_LETTERS,
  cms1500PatientControlNumber,
  cms1500StateCode,
  POLARIS_BILLING_PROVIDER,
  withPolarisBillingProvider,
} from './cms1500-form';

const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';
const NUCC_TAXONOMY_SYSTEM = 'http://nucc.org/provider-taxonomy';
const STEDI_PAYER_NETWORK_SYSTEM = 'https://www.stedi.com/healthcare/network';
const BIRTHSEX_EXTENSION_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';
const DEFAULT_PLACE_OF_SERVICE = '02';

/** Source records a CMS-1500 draft is prefilled from. Everything is optional — gaps stay blank for validation. */
export interface Cms1500AutofillInput {
  patient?: Patient;
  /** The policy holder when it isn't the patient (`Coverage.subscriber`). */
  subscriber?: Patient | RelatedPerson;
  coverage?: Coverage;
  payor?: Organization;
  practitioner?: Practitioner;
  practitionerRole?: PractitionerRole;
  billingOrganization?: Organization;
  encounter?: Encounter;
  /** ICD-10-CM codes, principal first. */
  diagnoses: string[];
  /** Billable lines — `charge` is the line total. */
  lines: { cpt: string; modifiers?: string[]; units?: number; charge?: number; serviceDate?: string }[];
  placeOfServiceCode?: string;
  /** Dollars the patient already paid toward this claim (box 29). */
  patientPaid?: number;
  submitterOrganizationName?: string;
  submitterPhone?: string;
}

function identifier(
  resource: { identifier?: { system?: string; value?: string }[] } | undefined,
  system: string
): string {
  return resource?.identifier?.find((entry) => entry.system === system)?.value ?? '';
}

function name(source: HumanName | undefined): Cms1500Name {
  return {
    last: source?.family ?? '',
    first: source?.given?.[0] ?? '',
    middle: source?.given?.slice(1).join(' ') ?? '',
    suffix: source?.suffix?.[0] ?? '',
  };
}

function address(source: Address | undefined): Cms1500Address {
  return {
    line1: source?.line?.[0] ?? '',
    line2: source?.line?.[1] ?? '',
    city: source?.city ?? '',
    state: cms1500StateCode(source?.state ?? ''),
    zip: source?.postalCode ?? '',
  };
}

function sex(patient: Patient | RelatedPerson | undefined): Cms1500Sex {
  const birthsex = patient?.extension?.find((extension) => extension.url === BIRTHSEX_EXTENSION_URL);
  const code = birthsex?.valueCode ?? birthsex?.valueCoding?.code;
  if (code === 'M' || code === 'F') {
    return code;
  }
  if (patient?.gender === 'male') {
    return 'M';
  }
  if (patient?.gender === 'female') {
    return 'F';
  }
  return code || patient?.gender ? 'U' : '';
}

function relationship(coverage: Coverage | undefined): Cms1500Relationship {
  const code = (coverage?.relationship?.coding?.[0]?.code ?? coverage?.relationship?.text ?? 'self').toLowerCase();
  if (code === 'self' || code === 'spouse' || code === 'child') {
    return code;
  }
  return 'other';
}

function phone(resource: { telecom?: { system?: string; value?: string }[] } | undefined): string {
  return resource?.telecom?.find((telecom) => telecom.system === 'phone')?.value ?? '';
}

/**
 * Prefill a CMS-1500 form from the records a claim is built from. Only fields with a real source
 * are filled; nothing gets filler values (no `00000` ZIPs, no `U` sex for a blank record), so gaps
 * surface as validation issues instead of being transmitted.
 */
export function autofillCms1500Form(input: Cms1500AutofillInput): Cms1500Form {
  const form = blankCms1500Form();
  const { patient, coverage, payor, practitioner, practitionerRole, billingOrganization, encounter } = input;

  form.payer = { id: identifier(payor, STEDI_PAYER_NETWORK_SYSTEM), name: payor?.name ?? '' };
  form.respLevel = ['P', 'S', 'T'][Math.min(Math.max((coverage?.order ?? 1) - 1, 0), 2)];
  form.memberId = coverage?.subscriberId ?? '';
  form.rel = relationship(coverage);

  const patientName = name(patient?.name?.[0]);
  const patientAddress = address(patient?.address?.[0]);
  form.pat = { ...patientName, ...patientAddress, dob: patient?.birthDate ?? '', sex: sex(patient) };

  const holder = form.rel === 'self' ? patient : input.subscriber;
  const groupClass = coverage?.class?.find((entry) => entry.type?.coding?.[0]?.code === 'group');
  const planClass = coverage?.class?.find((entry) => entry.type?.coding?.[0]?.code === 'plan');
  form.ins = {
    ...name(holder?.name?.[0]),
    ...address(holder?.address?.[0]),
    dob: holder?.birthDate ?? '',
    sex: sex(holder),
    group: groupClass?.value ?? '',
    ocq: '',
    ocid: '',
    planName: planClass?.name ?? planClass?.value ?? '',
  };

  if (practitioner) {
    form.prov['82'] = {
      ...form.prov['82'],
      ...name(practitioner.name?.[0]),
      npi: identifier(practitioner, NPI_SYSTEM),
      taxonomy:
        practitionerRole?.specialty
          ?.flatMap((specialty) => specialty.coding ?? [])
          .find((coding) => coding.system === NUCC_TAXONOMY_SYSTEM)?.code ?? '',
    };
  }

  const diagnoses = input.diagnoses.map((code) => code.trim().toUpperCase()).filter(Boolean);
  form.dx = CMS1500_LETTERS.map((_, index) => diagnoses[index] ?? '');
  const pointers = CMS1500_LETTERS.slice(0, Math.min(4, diagnoses.length));
  const serviceDate = encounter?.period?.start?.slice(0, 10) ?? '';
  const pos = input.placeOfServiceCode || DEFAULT_PLACE_OF_SERVICE;
  form.lines = input.lines.map((line) => {
    const mods = (line.modifiers ?? []).slice(0, 4);
    return {
      ...blankCms1500Line(pos),
      from: line.serviceDate ?? serviceDate,
      to: line.serviceDate ?? serviceDate,
      cpt: line.cpt,
      mods: [0, 1, 2, 3].map((index) => mods[index] ?? ''),
      ptrs: [...pointers],
      charge: line.charge != null ? line.charge.toFixed(2) : '',
      units: String(line.units ?? 1),
    };
  });

  form.acct = encounter?.id ? cms1500PatientControlNumber(encounter.id) : '';
  form.amtPaid = input.patientPaid && input.patientPaid > 0 ? input.patientPaid.toFixed(2) : '';

  form.sub = {
    ...form.sub,
    type: 'org',
    org: input.submitterOrganizationName || billingOrganization?.name || '',
    // The tenant Organization usually has no phone; Stedi requires a submitter contact, so fall back to Polaris'.
    phone: input.submitterPhone || phone(billingOrganization) || POLARIS_BILLING_PROVIDER.bill.phone,
  };
  return withPolarisBillingProvider(form);
}
